import { cn } from "@/lib/utils";

export type RiskLevel = "Low" | "Medium" | "High";

export const scoreToLevel = (score: number): RiskLevel => {
  if (score >= 15) return "High";
  if (score >= 8) return "Medium";
  return "Low";
};

const levelStyles: Record<RiskLevel, string> = {
  Low: "bg-green-100 text-green-800 border-green-300",
  Medium: "bg-amber-100 text-amber-800 border-amber-300",
  High: "bg-red-100 text-red-800 border-red-300",
};

interface Props {
  /** Number to display (risk score or count). */
  value: number;
  /** Override the colour level. If omitted, derives from value via score bands. */
  level?: RiskLevel;
  size?: "sm" | "md";
  /** When true, show only the band word (Low/Medium/High) without the number. */
  labelOnly?: boolean;
  className?: string;
}

/**
 * Stacked risk badge: number on top (centered), level word below (centered).
 * When labelOnly is true, shows only the level word as a centered pill.
 */
export function RiskBadge({ value, level, size = "md", labelOnly = false, className }: Props) {
  const lvl = level ?? scoreToLevel(value);
  if (labelOnly) {
    const pad = size === "sm" ? "px-2.5 py-0.5 text-xs" : "px-3 py-1 text-sm";
    return (
      <span
        className={cn(
          "inline-flex items-center justify-center rounded-full border font-semibold uppercase tracking-wide",
          levelStyles[lvl],
          pad,
          className
        )}
      >
        {lvl}
      </span>
    );
  }
  const dims =
    size === "sm"
      ? "min-w-[44px] px-2 py-0.5"
      : "min-w-[52px] px-2.5 py-1";
  const num = size === "sm" ? "text-sm" : "text-base";
  const lbl = size === "sm" ? "text-[9px]" : "text-[10px]";
  return (
    <span
      className={cn(
        "inline-flex flex-col items-center justify-center rounded-full border leading-tight",
        levelStyles[lvl],
        dims,
        className
      )}
    >
      <span className={cn("font-semibold", num)}>{value}</span>
      <span className={cn("uppercase tracking-wide font-medium", lbl)}>{lvl}</span>
    </span>
  );
}

